import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { toast } from 'sonner';
import {
  ArrowLeft,
  ArrowClockwise,
  Lock,
  LockOpen,
  Warning,
  CheckCircle,
  XCircle,
  Info,
  Database,
  UploadSimple,
  CloudArrowDown,
  CaretDown,
  CaretRight,
  Clock,
  ShieldCheck,
} from '@phosphor-icons/react';
import api from '../lib/api';

// ---------------------------------------------------------------------------
// Status pill → visual config
// ---------------------------------------------------------------------------

const PILL_CONFIG = {
  API_TRUTH:        { label: 'API Truth',       bg: 'bg-emerald-500/10', text: 'text-emerald-400', border: 'border-emerald-500/30', dot: 'bg-emerald-500' },
  UPLOAD_TRUTH:     { label: 'Upload Truth',    bg: 'bg-blue-500/10',    text: 'text-blue-400',    border: 'border-blue-500/30',    dot: 'bg-blue-500' },
  AUTO_RESOLVED:    { label: 'Auto Resolved',   bg: 'bg-cyan-500/10',    text: 'text-cyan-400',    border: 'border-cyan-500/30',    dot: 'bg-cyan-500' },
  MANUAL_OVERRIDE:  { label: 'Manual Override', bg: 'bg-amber-500/10',   text: 'text-amber-400',   border: 'border-amber-500/30',   dot: 'bg-amber-500' },
  LOCKED:           { label: 'Locked',          bg: 'bg-purple-500/10',  text: 'text-purple-400',  border: 'border-purple-500/30',  dot: 'bg-purple-500' },
  MISSING_DATA:     { label: 'Missing Data',    bg: 'bg-zinc-500/10',    text: 'text-zinc-500',    border: 'border-zinc-500/30',    dot: 'bg-zinc-600' },
};

const SOURCE_LABEL = {
  POS: 'API (POS)',
  TRANSACTION_UPLOAD: 'Transaction Upload',
  ITEM_UPLOAD: 'Item Upload',
};

const VARIANCE_PILL = {
  MATCHED: { label: 'Matched', bg: 'bg-emerald-500/10', text: 'text-emerald-400' },
  MINOR_VARIANCE: { label: 'Minor', bg: 'bg-blue-500/10', text: 'text-blue-400' },
  REVIEW: { label: 'Review', bg: 'bg-amber-500/10', text: 'text-amber-400' },
  BLOCKED: { label: 'Blocked', bg: 'bg-red-500/10', text: 'text-red-400' },
};

// ---------------------------------------------------------------------------
// Helpers
// ---------------------------------------------------------------------------

import { fmtLocalISO } from '../lib/date';

const isoDate = fmtLocalISO;

const defaultRange = () => {
  const end = new Date();
  const start = new Date();
  start.setDate(end.getDate() - 13); // last 14 days
  return { start_date: isoDate(start), end_date: isoDate(end) };
};

const fmtTs = (ts) => {
  if (!ts) return '—';
  try {
    return new Date(ts).toLocaleString();
  } catch {
    return ts;
  }
};

// ---------------------------------------------------------------------------
// Status pill
// ---------------------------------------------------------------------------

const StatusPill = ({ pill }) => {
  const cfg = PILL_CONFIG[pill] || PILL_CONFIG.AUTO_RESOLVED;
  return (
    <span
      className={`inline-flex items-center gap-1.5 px-2 py-0.5 rounded text-[10px] font-medium uppercase tracking-wider border ${cfg.bg} ${cfg.text} ${cfg.border}`}
      data-testid={`status-pill-${pill}`}
    >
      <span className={`w-1.5 h-1.5 rounded-full ${cfg.dot}`} />
      {cfg.label}
    </span>
  );
};

// ---------------------------------------------------------------------------
// Evidence icons
// ---------------------------------------------------------------------------

const EvidenceChips = ({ available, counts }) => {
  const chips = [
    { key: 'POS', icon: CloudArrowDown, label: 'API', count: counts.POS || 0 },
    { key: 'TRANSACTION_UPLOAD', icon: UploadSimple, label: 'Tx Upload', count: counts.TRANSACTION_UPLOAD || 0 },
    { key: 'ITEM_UPLOAD', icon: Database, label: 'Item Upload', count: counts.ITEM_UPLOAD || 0 },
  ];
  return (
    <div className="flex items-center gap-1.5">
      {chips.map((c) => {
        const has = available.includes(c.key);
        const Icon = c.icon;
        return (
          <span
            key={c.key}
            title={`${c.label}: ${c.count} rows`}
            className={`inline-flex items-center gap-1 px-1.5 py-0.5 rounded text-[10px] font-mono border ${
              has
                ? 'bg-white/[0.04] border-white/10 text-zinc-300'
                : 'bg-transparent border-white/5 text-zinc-700'
            }`}
            data-testid={`evidence-${c.key}`}
          >
            <Icon size={10} weight={has ? 'fill' : 'regular'} />
            {c.count}
          </span>
        );
      })}
    </div>
  );
};

// ---------------------------------------------------------------------------
// Override form
// ---------------------------------------------------------------------------

const OverrideForm = ({ day, outletId, onSaved, onCancel }) => {
  const currentOverride = day.override;
  const [forcedSource, setForcedSource] = useState(
    currentOverride?.forced_source || day.current_source || 'POS',
  );
  const [reason, setReason] = useState(currentOverride?.reason || '');
  const [locked, setLocked] = useState(currentOverride ? !!currentOverride.locked : true);
  const [submitting, setSubmitting] = useState(false);

  const available = day.available_sources || [];
  const hasPos = available.includes('POS');
  const hasTxUpload = available.includes('TRANSACTION_UPLOAD');
  const hasItemUpload = available.includes('ITEM_UPLOAD');

  const sourceOpts = [
    { value: 'POS', label: SOURCE_LABEL.POS, disabled: !hasPos, note: hasPos ? `${day.source_counts?.POS || 0} API rows` : 'No API data for this day' },
    { value: 'TRANSACTION_UPLOAD', label: SOURCE_LABEL.TRANSACTION_UPLOAD, disabled: !hasTxUpload, note: hasTxUpload ? `${day.source_counts?.TRANSACTION_UPLOAD || 0} rows` : 'No upload for this day' },
    { value: 'ITEM_UPLOAD', label: SOURCE_LABEL.ITEM_UPLOAD, disabled: !hasItemUpload, note: hasItemUpload ? `${day.source_counts?.ITEM_UPLOAD || 0} rows` : 'No item upload for this day' },
  ];

  // Warn when both sources exist and they differ
  const bothExist = hasPos && hasTxUpload;
  const differsMeaningful = bothExist && day.variance && day.variance.variance_status && day.variance.variance_status !== 'MATCHED';

  const handleSubmit = async () => {
    if (!reason.trim()) {
      toast.error('Reason is required to override a day.');
      return;
    }
    const opt = sourceOpts.find((o) => o.value === forcedSource);
    if (opt?.disabled) {
      toast.error(`Cannot force ${SOURCE_LABEL[forcedSource]} — no data for this day.`);
      return;
    }
    setSubmitting(true);
    try {
      await api.post('/source-resolution/override', {
        outlet_id: outletId,
        business_date: day.business_date,
        forced_source: forcedSource,
        reason: reason.trim(),
        locked,
      });
      toast.success(`Override saved for ${day.business_date}`);
      onSaved();
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Failed to save override');
    } finally {
      setSubmitting(false);
    }
  };

  const handleRemove = async () => {
    setSubmitting(true);
    try {
      await api.delete('/source-resolution/override', {
        data: { outlet_id: outletId, business_date: day.business_date },
      });
      toast.success(`Override removed for ${day.business_date} — returns to auto resolution`);
      onSaved();
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Failed to remove override');
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="space-y-4" data-testid="override-form">
      <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
        {sourceOpts.map((o) => (
          <button
            key={o.value}
            type="button"
            disabled={o.disabled}
            onClick={() => !o.disabled && setForcedSource(o.value)}
            data-testid={`override-source-${o.value}`}
            className={`text-left p-3 rounded-md border text-xs transition-colors ${
              forcedSource === o.value
                ? 'bg-cyan-500/10 border-cyan-500/40 text-cyan-300'
                : 'bg-white/[0.03] border-white/5 text-zinc-300 hover:bg-white/[0.05]'
            } ${o.disabled ? 'opacity-40 cursor-not-allowed' : ''}`}
          >
            <div className="font-medium">{o.label}</div>
            <div className="mt-1 font-mono text-[10px] text-zinc-500">{o.note}</div>
          </button>
        ))}
      </div>

      <div>
        <label className="block text-[10px] uppercase tracking-wider text-zinc-500 mb-1.5">
          Reason <span className="text-red-400">*</span>
        </label>
        <textarea
          value={reason}
          onChange={(e) => setReason(e.target.value)}
          rows={2}
          placeholder="Why are you overriding the source for this day? (required)"
          className="w-full px-3 py-2 rounded-md bg-black/30 border border-white/10 text-xs text-white placeholder:text-zinc-600 focus:outline-none focus:border-cyan-500/40"
          data-testid="override-reason"
        />
      </div>

      <label className="flex items-center gap-2 text-xs text-zinc-300 cursor-pointer">
        <input
          type="checkbox"
          checked={locked}
          onChange={(e) => setLocked(e.target.checked)}
          className="rounded border-zinc-600"
          data-testid="override-locked-toggle"
        />
        <Lock size={12} weight="fill" className="text-amber-400" />
        Lock this decision (prevent auto resolution from changing it)
      </label>

      {differsMeaningful && (
        <div className="flex items-start gap-2 p-2.5 rounded-md bg-amber-500/5 border border-amber-500/20 text-[11px] text-amber-300">
          <Warning size={14} weight="fill" className="shrink-0 mt-0.5" />
          <span>
            API and Upload differ materially for this day (variance:{' '}
            <span className="font-mono">{day.variance.variance_status}</span>
            {typeof day.variance.summary_variance_pct === 'number'
              ? ` — ${day.variance.summary_variance_pct.toFixed(2)}%`
              : ''}
            ). Confirm before switching.
          </span>
        </div>
      )}

      <div className="flex items-center justify-end gap-2 pt-2 border-t border-white/5">
        {currentOverride && (
          <button
            onClick={handleRemove}
            disabled={submitting}
            className="px-3 py-1.5 rounded-md text-xs font-medium text-zinc-400 bg-white/5 hover:bg-white/10 transition-colors disabled:opacity-50"
            data-testid="override-remove-btn"
          >
            <LockOpen size={12} weight="fill" className="inline mr-1" />
            Return to Auto
          </button>
        )}
        <button
          onClick={onCancel}
          disabled={submitting}
          className="px-3 py-1.5 rounded-md text-xs font-medium text-zinc-400 hover:text-white transition-colors"
          data-testid="override-cancel-btn"
        >
          Cancel
        </button>
        <button
          onClick={handleSubmit}
          disabled={submitting || !reason.trim()}
          className="px-3 py-1.5 rounded-md text-xs font-medium text-cyan-300 bg-cyan-500/10 border border-cyan-500/30 hover:bg-cyan-500/20 transition-colors disabled:opacity-50"
          data-testid="override-save-btn"
        >
          {submitting ? 'Saving...' : currentOverride ? 'Update Override' : 'Save Override'}
        </button>
      </div>
    </div>
  );
};

// ---------------------------------------------------------------------------
// Row drawer (evidence + audit + form)
// ---------------------------------------------------------------------------

const DayDetailDrawer = ({ day, outletId, onRefetch }) => {
  const [logs, setLogs] = useState([]);
  const [logsLoading, setLogsLoading] = useState(true);
  const [showForm, setShowForm] = useState(false);

  const fetchLogs = useCallback(async () => {
    setLogsLoading(true);
    try {
      const res = await api.get(`/source-resolution/audit-log?outlet_id=${outletId}&limit=50`);
      const all = res.data.logs || [];
      const forDay = all.filter((l) => l.business_date === day.business_date);
      setLogs(forDay);
    } catch {
      // silent
    } finally {
      setLogsLoading(false);
    }
  }, [outletId, day.business_date]);

  useEffect(() => {
    fetchLogs();
  }, [fetchLogs]);

  return (
    <div className="p-4 bg-black/20 border-t border-white/5 space-y-4" data-testid={`day-drawer-${day.business_date}`}>
      {/* Evidence grid */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
        <div className="p-2.5 rounded bg-white/[0.03] border border-white/5">
          <div className="text-[10px] uppercase tracking-wider text-zinc-500">Current Source</div>
          <div className="mt-1 text-sm text-white font-medium">{day.current_source ? SOURCE_LABEL[day.current_source] : '—'}</div>
        </div>
        <div className="p-2.5 rounded bg-white/[0.03] border border-white/5">
          <div className="text-[10px] uppercase tracking-wider text-zinc-500">Resolution Reason</div>
          <div className="mt-1 text-xs text-zinc-300 font-mono">{day.resolution_reason || '—'}</div>
        </div>
        <div className="p-2.5 rounded bg-white/[0.03] border border-white/5">
          <div className="text-[10px] uppercase tracking-wider text-zinc-500">Confidence</div>
          <div className="mt-1 text-sm text-white font-mono">
            {day.confidence != null ? `${Math.round(day.confidence * 100)}%` : '—'}
          </div>
        </div>
        <div className="p-2.5 rounded bg-white/[0.03] border border-white/5">
          <div className="text-[10px] uppercase tracking-wider text-zinc-500">Variance</div>
          <div className="mt-1">
            {day.variance ? (
              <span
                className={`px-1.5 py-0.5 rounded text-[10px] font-medium uppercase tracking-wider ${
                  VARIANCE_PILL[day.variance.variance_status]?.bg || 'bg-zinc-500/10'
                } ${VARIANCE_PILL[day.variance.variance_status]?.text || 'text-zinc-400'}`}
              >
                {VARIANCE_PILL[day.variance.variance_status]?.label || day.variance.variance_status}
              </span>
            ) : (
              <span className="text-xs text-zinc-500">Not computed</span>
            )}
          </div>
        </div>
      </div>

      {/* Current override block */}
      {day.override && (
        <div className="p-3 rounded-md bg-amber-500/5 border border-amber-500/20">
          <div className="flex items-center gap-2 mb-1">
            <Lock size={13} weight="fill" className="text-amber-400" />
            <span className="text-xs font-medium text-amber-300">Active Override</span>
            {day.override.locked && <span className="ml-1 text-[10px] font-mono text-amber-500">LOCKED</span>}
          </div>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-2 text-[11px]">
            <div>
              <span className="text-zinc-500">Forced source: </span>
              <span className="text-zinc-200 font-mono">{SOURCE_LABEL[day.override.forced_source]}</span>
            </div>
            <div>
              <span className="text-zinc-500">By: </span>
              <span className="text-zinc-200 font-mono truncate inline-block max-w-[160px]">{day.override.created_by}</span>
            </div>
            <div>
              <span className="text-zinc-500">At: </span>
              <span className="text-zinc-200 font-mono">{fmtTs(day.override.created_at)}</span>
            </div>
          </div>
          <div className="mt-2 text-[11px] text-zinc-300">
            <span className="text-zinc-500">Reason: </span>
            <span className="italic">{day.override.reason || '—'}</span>
          </div>
        </div>
      )}

      {/* Action row */}
      <div className="flex items-center justify-between">
        <div className="text-[11px] text-zinc-500">
          Override applies to <span className="font-mono text-zinc-300">{day.business_date}</span> only. Outlet mode and cutover policy are unchanged.
        </div>
        {!showForm && (
          <button
            onClick={() => setShowForm(true)}
            className="px-3 py-1.5 rounded-md text-xs font-medium text-cyan-300 bg-cyan-500/10 border border-cyan-500/30 hover:bg-cyan-500/20 transition-colors"
            data-testid={`open-override-form-${day.business_date}`}
          >
            {day.override ? 'Edit Override' : 'Override This Day'}
          </button>
        )}
      </div>

      {showForm && (
        <OverrideForm
          day={day}
          outletId={outletId}
          onSaved={() => {
            setShowForm(false);
            onRefetch();
          }}
          onCancel={() => setShowForm(false)}
        />
      )}

      {/* Audit log */}
      <div>
        <div className="flex items-center gap-2 mb-2">
          <Clock size={12} className="text-zinc-500" />
          <span className="text-[10px] uppercase tracking-wider text-zinc-500">Audit Log (this day)</span>
        </div>
        {logsLoading ? (
          <div className="text-xs text-zinc-500">Loading...</div>
        ) : logs.length === 0 ? (
          <div className="text-xs text-zinc-500">No audit entries yet.</div>
        ) : (
          <div className="space-y-1.5">
            {logs.map((l, i) => (
              <div key={i} className="flex items-center gap-2 text-[11px] font-mono text-zinc-400">
                <span className="text-zinc-600">{fmtTs(l.timestamp)}</span>
                <span className="px-1.5 py-0.5 rounded bg-white/[0.04] text-zinc-300">{l.action}</span>
                <span className="text-zinc-500">
                  {l.previous_source || '—'} → {l.new_source || '—'}
                </span>
                <span className="text-zinc-600 truncate">{l.reason}</span>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
};

// ---------------------------------------------------------------------------
// Day row
// ---------------------------------------------------------------------------

const DayRow = ({ day, expanded, onToggle, outletId, onRefetch }) => {
  const reasonShort = day.resolution_reason || (day.status_pill === 'MISSING_DATA' ? 'no data' : '—');
  return (
    <>
      <button
        onClick={onToggle}
        className={`w-full grid grid-cols-[24px_110px_110px_140px_1fr_120px_90px] items-center gap-2 px-3 py-2.5 text-left text-xs transition-colors ${
          expanded ? 'bg-white/[0.04]' : 'hover:bg-white/[0.02]'
        }`}
        data-testid={`day-row-${day.business_date}`}
      >
        <span className="text-zinc-500">
          {expanded ? <CaretDown size={12} /> : <CaretRight size={12} />}
        </span>
        <span className="font-mono text-zinc-300">{day.business_date}</span>
        <StatusPill pill={day.status_pill} />
        <span className="text-zinc-300 truncate">{day.current_source ? SOURCE_LABEL[day.current_source] : '—'}</span>
        <EvidenceChips available={day.available_sources || []} counts={day.source_counts || {}} />
        {day.variance ? (
          <span
            className={`px-1.5 py-0.5 rounded text-[10px] font-medium uppercase tracking-wider ${
              VARIANCE_PILL[day.variance.variance_status]?.bg || 'bg-zinc-500/10'
            } ${VARIANCE_PILL[day.variance.variance_status]?.text || 'text-zinc-400'}`}
          >
            {VARIANCE_PILL[day.variance.variance_status]?.label || day.variance.variance_status}
          </span>
        ) : (
          <span className="text-zinc-600 text-[10px] font-mono">no variance</span>
        )}
        <span className="text-zinc-500 font-mono text-[10px] truncate" title={reasonShort}>
          {reasonShort}
        </span>
      </button>
      {expanded && <DayDetailDrawer day={day} outletId={outletId} onRefetch={onRefetch} />}
    </>
  );
};

// ---------------------------------------------------------------------------
// Main panel
// ---------------------------------------------------------------------------

const DayOverridesPanel = ({ outlets, onBack }) => {
  const [outletId, setOutletId] = useState(outlets?.[0]?.outlet_id || '');
  const [range, setRange] = useState(defaultRange());
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [expanded, setExpanded] = useState(null);
  const [filter, setFilter] = useState('all'); // all | overridden | has_data | missing

  // Sync outletId when outlets prop arrives late
  useEffect(() => {
    if (!outletId && outlets && outlets.length > 0) {
      setOutletId(outlets[0].outlet_id);
    }
  }, [outlets, outletId]);

  const fetchOverview = useCallback(async () => {
    if (!outletId) return;
    setLoading(true);
    try {
      const res = await api.get(
        `/source-resolution/day-overview?outlet_id=${encodeURIComponent(outletId)}&start_date=${range.start_date}&end_date=${range.end_date}`,
      );
      setData(res.data);
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Failed to load day overview');
      setData(null);
    } finally {
      setLoading(false);
    }
  }, [outletId, range]);

  useEffect(() => {
    fetchOverview();
  }, [fetchOverview]);

  const days = data?.days || [];
  const filteredDays = useMemo(() => {
    if (filter === 'all') return days;
    if (filter === 'overridden') return days.filter((d) => d.override);
    if (filter === 'has_data') return days.filter((d) => (d.available_sources || []).length > 0);
    if (filter === 'missing') return days.filter((d) => (d.available_sources || []).length === 0);
    return days;
  }, [days, filter]);

  const counts = useMemo(() => {
    const c = { overridden: 0, missing: 0, has_data: 0 };
    for (const d of days) {
      if (d.override) c.overridden += 1;
      if ((d.available_sources || []).length === 0) c.missing += 1;
      else c.has_data += 1;
    }
    return c;
  }, [days]);

  const pillCounts = useMemo(() => {
    const c = {};
    for (const d of days) c[d.status_pill] = (c[d.status_pill] || 0) + 1;
    return c;
  }, [days]);

  return (
    <div className="space-y-5" data-testid="day-overrides-panel">
      {/* Header */}
      <div className="flex items-center gap-4">
        <button
          onClick={onBack}
          className="p-2 rounded-md hover:bg-white/5 text-zinc-400 hover:text-white transition-colors"
          data-testid="back-from-overrides-btn"
        >
          <ArrowLeft size={20} />
        </button>
        <div className="flex-1">
          <div className="flex items-center gap-2">
            <ShieldCheck size={20} weight="duotone" className="text-cyan-400" />
            <h2 className="text-lg font-medium font-['Outfit']">Day Overrides</h2>
          </div>
          <p className="text-xs text-zinc-500 mt-0.5">
            Per-day canonical source control. Overrides are day-scoped only — outlet mode and cutover policy are not affected.
          </p>
        </div>
        <button
          onClick={fetchOverview}
          className="p-2 rounded-md hover:bg-white/5 text-zinc-400 hover:text-white transition-colors"
          data-testid="overrides-refresh-btn"
        >
          <ArrowClockwise size={18} />
        </button>
      </div>

      {/* Outlet + range controls */}
      <div className="flex flex-wrap items-end gap-3">
        <div className="flex flex-col">
          <label className="text-[10px] uppercase tracking-wider text-zinc-500 mb-1">Outlet</label>
          <select
            value={outletId}
            onChange={(e) => {
              setOutletId(e.target.value);
              setExpanded(null);
            }}
            className="px-3 py-1.5 rounded-md bg-black/30 border border-white/10 text-xs text-white focus:outline-none focus:border-cyan-500/40 min-w-[280px]"
            data-testid="overrides-outlet-select"
          >
            {(outlets || []).map((o) => (
              <option key={o.outlet_id} value={o.outlet_id}>
                {o.outlet_id}
              </option>
            ))}
          </select>
        </div>
        <div className="flex flex-col">
          <label className="text-[10px] uppercase tracking-wider text-zinc-500 mb-1">From</label>
          <input
            type="date"
            value={range.start_date}
            onChange={(e) => setRange((r) => ({ ...r, start_date: e.target.value }))}
            className="px-3 py-1.5 rounded-md bg-black/30 border border-white/10 text-xs text-white focus:outline-none focus:border-cyan-500/40"
            data-testid="overrides-start-date"
          />
        </div>
        <div className="flex flex-col">
          <label className="text-[10px] uppercase tracking-wider text-zinc-500 mb-1">To</label>
          <input
            type="date"
            value={range.end_date}
            onChange={(e) => setRange((r) => ({ ...r, end_date: e.target.value }))}
            className="px-3 py-1.5 rounded-md bg-black/30 border border-white/10 text-xs text-white focus:outline-none focus:border-cyan-500/40"
            data-testid="overrides-end-date"
          />
        </div>
        {data?.config && (
          <div className="flex flex-col text-[11px] text-zinc-400 pb-1">
            <span>
              Mode: <span className="font-mono text-zinc-300">{data.config.mode}</span>
            </span>
            <span>
              Cutover: <span className="font-mono text-zinc-300">{data.config.cutover_date || 'not set'}</span>
            </span>
          </div>
        )}
        {data?.connector && (
          <div className="flex flex-col text-[11px] text-zinc-400 pb-1">
            <span>
              Connector: <span className="font-mono text-zinc-300">{data.connector.status || '—'}</span>
            </span>
            <span>
              Last sync: <span className="font-mono text-zinc-300">{fmtTs(data.connector.last_sync_at)}</span>
            </span>
          </div>
        )}
      </div>

      {/* Filter pills */}
      <div className="flex flex-wrap gap-2">
        {[
          { key: 'all', label: 'All Days', count: days.length },
          { key: 'overridden', label: 'Overridden', count: counts.overridden },
          { key: 'has_data', label: 'With Data', count: counts.has_data },
          { key: 'missing', label: 'Missing Data', count: counts.missing },
        ].map((f) => (
          <button
            key={f.key}
            onClick={() => setFilter(f.key)}
            className={`px-3 py-1.5 rounded-md text-xs font-medium transition-colors ${
              filter === f.key
                ? 'bg-white/10 text-white'
                : 'bg-white/[0.03] text-zinc-500 hover:text-zinc-300'
            }`}
            data-testid={`overrides-filter-${f.key}`}
          >
            {f.label} <span className="ml-1 font-mono">{f.count}</span>
          </button>
        ))}
        <div className="flex items-center gap-1.5 ml-auto text-[10px] text-zinc-500">
          {Object.entries(PILL_CONFIG).map(([k, cfg]) => {
            const c = pillCounts[k] || 0;
            if (!c) return null;
            return (
              <span key={k} className={`px-1.5 py-0.5 rounded border ${cfg.bg} ${cfg.text} ${cfg.border}`}>
                {cfg.label} <span className="ml-0.5 font-mono">{c}</span>
              </span>
            );
          })}
        </div>
      </div>

      {/* Table */}
      <div className="surface-base rounded-lg overflow-hidden border border-white/5">
        <div className="grid grid-cols-[24px_110px_110px_140px_1fr_120px_90px] gap-2 px-3 py-2 text-[10px] uppercase tracking-wider text-zinc-500 border-b border-white/5 bg-black/30">
          <span />
          <span>Date</span>
          <span>Status</span>
          <span>Current Source</span>
          <span>Evidence</span>
          <span>Variance</span>
          <span>Reason</span>
        </div>

        {loading ? (
          <div className="p-12 text-center text-sm text-zinc-400">Loading day overview...</div>
        ) : filteredDays.length === 0 ? (
          <div className="p-12 text-center">
            <Info size={32} weight="duotone" className="mx-auto text-zinc-600 mb-2" />
            <p className="text-sm text-zinc-400">No days match this filter.</p>
          </div>
        ) : (
          <div className="divide-y divide-white/[0.04]">
            {filteredDays.map((day) => (
              <DayRow
                key={day.business_date}
                day={day}
                outletId={outletId}
                expanded={expanded === day.business_date}
                onToggle={() =>
                  setExpanded((cur) => (cur === day.business_date ? null : day.business_date))
                }
                onRefetch={fetchOverview}
              />
            ))}
          </div>
        )}
      </div>

      {/* Rule footnote */}
      <div className="text-[11px] text-zinc-500 flex items-start gap-2 p-3 rounded-md bg-white/[0.02] border border-white/5">
        <Info size={12} className="shrink-0 mt-0.5 text-zinc-500" />
        <span>
          This surface is for <strong>day-scoped truth control only</strong>. To change outlet-wide mode, cutover policy, or
          connector setup, use the Source Health or Integration Copilot surfaces.
        </span>
      </div>
    </div>
  );
};

export default DayOverridesPanel;
